import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useAppSelector } from 'store/hook';
import {
  generateShiftData,
  getShiftDetails,
  ShiftDetails,
} from 'views/Utilization/utils';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { formatStartEndDateUTC } from 'utils/helpers/dateTime';
import { getCustomerShiftsUTCForDate } from 'utils/helpers/customerShiftHelpers';

interface useStartEndDatesProps {
  showShiftWise: boolean;
  startDate: Date;
  endDate: Date;
}

interface useStartDateProps {
  showShiftWise: boolean;
  startHour?: number;
  endHour?: number;
}

const format = 'YYYY-MM-DD';

const useStartEndDates = ({
  showShiftWise,
  startDate,
  endDate,
}: useStartEndDatesProps) => {
  const customerCode = useAppSelector((state) =>
    state.authReducer.customer.code.toLowerCase()
  );
  const customerTz = useAppSelector(
    (state) =>
      state.persistedReducer.customer.timezone ??
      state.authReducer.customer.timezone
  );
  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();

  // getCustomerShiftsUTCForDate reads this slice straight off the store
  // (store.getState()), not via useSelector — so without subscribing to it
  // here too, the memo below never recomputes when the shift-config fetch
  // (kicked off from CustomerSelector, in-flight while /modules navigates)
  // resolves after this hook has already mounted. Until some other dep
  // changes (e.g. the date picker), shiftsWithTime stays stuck on the
  // hardcoded ShiftDetails fallback for the whole session — the wrong shift
  // boundaries then feed the driver-event query and the "unknown" gap
  // window, so the first render can show no (or wrong) "Unknown Event"
  // segments until something else forces a recompute.
  const shiftConfigData = useAppSelector(
    (state) => state.customerShiftConfigReducer.data
  );

  const localStartDate = dayjs.tz(startDate, customerTz).format(format);

  const shiftsWithTime = useMemo(() => {
    if (localStartDate) {
      const utcShifts = getCustomerShiftsUTCForDate(
        customerCode,
        localStartDate
      );
      const shiftDetails =
        utcShifts.length > 0
          ? getShiftDetails(customerCode, utcShifts)
          : getShiftDetails(customerCode);
      const shiftsWithTime = generateShiftData(
        localStartDate,
        shiftDetails,
        customerTz
      );

      return shiftsWithTime;
    }
    return [];
  }, [customerCode, customerTz, localStartDate, shiftConfigData]);

  const startEndDateWithTime = useMemo(() => {
    if (!customerCode || !timezoneBasedOnDisplayMode) return null;

    const { startUtcISO, endUtcISO } = formatStartEndDateUTC(
      'Custom',
      startDate,
      endDate,
      timezoneBasedOnDisplayMode
    );

    if (!showShiftWise) {
      const startUtc = dayjs.utc(startUtcISO);
      const endUtc = dayjs.utc(endUtcISO);

      let difference = endUtc.diff(startUtc, 'day');
      difference = difference <= 1 ? 0 : difference;
      return {
        startDateWithTime: startUtc.format('YYYY-MM-DD HH:mm:ss'),
        endDateWithTime: endUtc.format('YYYY-MM-DD HH:mm:ss'),
        difference,
      };
    }
    const startDateWithTime = dayjs
      .utc(shiftsWithTime[0].startDate)
      .format('YYYY-MM-DD HH:mm:ss');
    const endDateWithTime = dayjs
      .utc(shiftsWithTime[shiftsWithTime.length - 1].endDate)
      .format('YYYY-MM-DD HH:mm:ss');
    return {
      startDateWithTime,
      endDateWithTime,
      difference: 0,
    };
  }, [
    startDate,
    endDate,
    showShiftWise,
    customerCode,
    timezoneBasedOnDisplayMode,
    shiftsWithTime,
  ]);

  return {
    // startHour,
    // endHour,
    ...startEndDateWithTime,
    shiftsWithTime,
  };
};

const useStartDate = ({
  showShiftWise,
  startHour: providedStartHour,
  endHour: providedEndHour,
}: useStartDateProps) => {
  const customerCode = useAppSelector((state) => state.authReducer).customer
    .code;
  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();

  const shifts = useMemo(() => {
    return ShiftDetails?.[customerCode?.toLowerCase()];
  }, [customerCode]);

  const startHour = useMemo(() => {
    if (providedStartHour !== undefined) {
      return providedStartHour;
    }
    if (showShiftWise) {
      return shifts?.[0]?.startHour ?? ShiftDetails?.others?.[0]?.startHour;
    }
    return 0;
  }, [shifts, showShiftWise, providedStartHour]);

  const endHour = useMemo(() => {
    if (providedEndHour !== undefined) {
      return providedEndHour;
    }
    if (showShiftWise) {
      return (
        shifts?.[shifts.length - 1]?.endHour ??
        ShiftDetails?.others?.[0]?.endHour
      );
    }
    return 0;
  }, [shifts, showShiftWise, providedEndHour]);

  const shiftsWithTime = useMemo(() => {
    const shiftDetails = getShiftDetails(customerCode);

    const currentDate = new Date();
    const localStartDate = dayjs
      .tz(currentDate, timezoneBasedOnDisplayMode)
      .format(format);

    if (localStartDate) {
      return generateShiftData(
        localStartDate,
        shiftDetails,
        timezoneBasedOnDisplayMode
      );
    }
    return [];
  }, [customerCode, timezoneBasedOnDisplayMode]);

  return {
    startHour,
    endHour,
    shiftsWithTime,
  };
};

export default useStartEndDates;
export { useStartDate };
